import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { HERO_LOCATIONS, HERO_ROUTES, HERO_COLORS, latLngToVec3 } from "./constants";

/**
 * Great-circle SLERP arcs between location pairs + animated data-packet
 * spheres that fly along each arc. Additive-blended violet/pink beams.
 */
export default function NetworkArcs({ radius = 1 }) {
  const locMap = useMemo(
    () => Object.fromEntries(HERO_LOCATIONS.map((l) => [l.id, l])),
    [],
  );

  const arcs = useMemo(() => {
    return HERO_ROUTES.map(([aId, bId], idx) => {
      const a = locMap[aId];
      const b = locMap[bId];
      const va = new THREE.Vector3(...latLngToVec3(a.lat, a.lng, radius));
      const vb = new THREE.Vector3(...latLngToVec3(b.lat, b.lng, radius));

      /* SLERP over 64 steps, pushed outward for arc height. */
      const angle  = va.angleTo(vb);
      const altMax = 0.30 + angle * 0.10;             // higher arcs for far pairs
      const steps  = 64;
      const points = new Array(steps + 1);
      const sinA   = Math.sin(angle);
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const w1 = Math.sin((1 - t) * angle) / sinA;
        const w2 = Math.sin(t * angle) / sinA;
        const p  = va.clone().multiplyScalar(w1).add(vb.clone().multiplyScalar(w2));
        const bump = Math.sin(Math.PI * t) * altMax;
        p.setLength(radius + bump);
        points[i] = p;
      }
      const curve = new THREE.CatmullRomCurve3(points);
      return { id: `${aId}-${bId}`, curve, offset: (idx * 0.083) % 1 };
    });
  }, [locMap, radius]);

  return (
    <group>
      {arcs.map((arc) => <Arc key={arc.id} {...arc} />)}
    </group>
  );
}

function Arc({ curve, offset }) {
  const lineRef   = useRef();
  const packetRef = useRef();

  const geometry = useMemo(() => {
    const pts = curve.getPoints(96);
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [curve]);

  useFrame(({ clock }) => {
    const t = (clock.getElapsedTime() * 0.12 + offset) % 1;
    if (packetRef.current) {
      const pos = curve.getPointAt(t);
      packetRef.current.position.copy(pos);
      packetRef.current.material.opacity = Math.sin(t * Math.PI);   // fade in/out along arc
    }
    if (lineRef.current) {
      lineRef.current.material.opacity = 0.30 + Math.sin(clock.getElapsedTime() * 1.2 + offset * 6) * 0.10;
    }
  });

  return (
    <group>
      <primitive
        object={new THREE.Line(
          geometry,
          new THREE.LineBasicMaterial({
            color: HERO_COLORS.purple,
            transparent: true,
            opacity: 0.35,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          }),
        )}
        ref={lineRef}
      />
      <mesh ref={packetRef}>
        <sphereGeometry args={[0.014, 12, 12]} />
        <meshBasicMaterial
          color={HERO_COLORS.pinkLight}
          transparent
          opacity={0.9}
          toneMapped={false}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
